import { is, getScrollParent, debounce } from './utils.mjs';

const DRAG_SCROLL_THRESHOLD = 20;

/**
 * Creates a document-level drag-and-drop system for reordering elements.
 *
 * @param {object} config
 * @param {string} config.handleSelector - CSS selector for drag handle (e.g. 'div.form-drag-drop')
 * @param {function} config.findDragged - (handleBtn: Element) => Element|null — find the dragged element from handle
 * @param {function} config.findContainer - (draggedEl: Element) => Element|null — find the parent container
 * @param {function} [config.canDrag] - (draggedEl: Element, container: Element) => boolean — guard check
 * @param {function} config.getItems - (container: Element) => Element[] — get all draggable items
 * @param {function} config.getIndex - (draggedEl: Element, items: Element[]) => number — index before drag
 * @param {string} config.previewClass - CSS class for floating preview element
 * @param {string} config.dropZoneClass - CSS class for drop zone placeholder
 * @param {function} config.cleanPreview - (clone: Element) => void — strip internals from preview clone
 * @param {function} config.onDragStart - (e: MouseEvent, draggedEl, container, items) => { topOffset, leftOffset, width }
 * @param {function} config.moveItem - (draggedEl: Element, newIndex: number, container: Element) => void
 * @param {function} config.onCleanup - (draggedEl: Element, container: Element) => void
 * @param {function} [config.getBoundsElement] - (container: Element) => Element — element for scroll/hover bounds (default: container)
 */
export function createDragAndDrop({
	handleSelector,
	findDragged,
	findContainer,
	canDrag,
	getItems,
	getIndex,
	previewClass,
	dropZoneClass,
	cleanPreview,
	onDragStart,
	moveItem,
	onCleanup,
	getBoundsElement,
}) {
	let draggingOrder = [];
	let previewElement = null;
	let container = null;
	let draggedElement = null;
	let draggedElementRect = null;
	let indexBeforeDragging = null;
	let scrollContainer = null;
	let originalScrollPosition = 0;

	const handleMouseMovementScroll = debounce((e, hoverItem) => {
		if (!container || !scrollContainer) return;

		const containerRect = scrollContainer.getBoundingClientRect();
		const boundsEl = getBoundsElement ? getBoundsElement(container) : container;
		const boundsRect = boundsEl.getBoundingClientRect();
		let scrollDirection = null;

		if (boundsRect.top - DRAG_SCROLL_THRESHOLD <= e.y && boundsRect.bottom + DRAG_SCROLL_THRESHOLD >= e.y) {
			if (containerRect.top + DRAG_SCROLL_THRESHOLD >= e.y) {
				scrollDirection = 'top';
			} else if (containerRect.bottom - DRAG_SCROLL_THRESHOLD <= e.y) {
				scrollDirection = 'bottom';
			}
		}

		if (!scrollDirection) return;

		if (scrollDirection === 'top' && e.movementY < 0) {
			scrollContainer.scrollTop -= hoverItem.clientHeight / 4;
		} else if (scrollDirection === 'bottom' && e.movementY > 0) {
			scrollContainer.scrollTop += hoverItem.clientHeight / 4;
		}

		if (
			(scrollDirection === 'top' && scrollContainer.scrollTop > boundsRect.top) ||
			(scrollDirection === 'bottom' && scrollContainer.scrollTop < boundsRect.bottom)
		) {
			requestAnimationFrame(() => handleMouseMovementScroll(e, hoverItem));
		}
	});

	function onMouseDown(e) {
		const dndButton = is(e.target, handleSelector) ? e.target : e.target.closest(handleSelector);

		if (e.button !== 0 || !dndButton) return;

		e.stopPropagation();
		draggedElement = findDragged(dndButton);
		if (!draggedElement) return;

		container = findContainer(draggedElement);
		if (!container) {
			draggedElement = null;
			return;
		}

		if (canDrag && !canDrag(draggedElement, container)) {
			draggedElement = null;
			container = null;
			return;
		}

		const items = getItems(container);
		if (items.length < 2) {
			draggedElement = null;
			container = null;
			return;
		}

		indexBeforeDragging = getIndex(draggedElement, items);
		scrollContainer = getScrollParent(container, 'y');

		const offsets = onDragStart(e, draggedElement, container, items);

		originalScrollPosition = scrollContainer?.scrollTop ?? 0;

		previewElement = draggedElement.cloneNode(true);
		cleanPreview(previewElement);
		previewElement.classList.add(previewClass);
		previewElement.style.top = `${offsets.topOffset}px`;
		previewElement.style.left = `${offsets.leftOffset}px`;
		previewElement.style.width = `${offsets.width}px`;
		previewElement.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
		container.appendChild(previewElement);

		draggedElement.classList.add(dropZoneClass);
		draggedElementRect = draggedElement.getBoundingClientRect();
		document.documentElement.classList.add('forman-dragging');

		draggingOrder = items.map((n) => {
			const rect = n.getBoundingClientRect();
			n.style.zIndex = '10';
			return { element: n, top: rect.top, bottom: rect.bottom, rect };
		});

		e.preventDefault();
	}

	function onMouseUp(e) {
		if (e.button !== 0 || !container || !draggedElement) return;

		moveItem(
			draggedElement,
			draggingOrder.findIndex((el) => el.element === draggedElement),
			container,
		);
		dragDropCleanup();
		e.preventDefault();
	}

	function onMouseMove(e) {
		if (!container || !draggedElement || e.button !== 0) return;

		const hoverItem = findHoverItem(e);

		previewElement.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
		if (hoverItem && hoverItem !== draggedElement) {
			const draggedIndex = draggingOrder.findIndex((item) => item.element === draggedElement);
			const itemIndex = draggingOrder.findIndex((item) => item.element === hoverItem);
			const minIndex = Math.min(itemIndex, indexBeforeDragging);
			const maxIndex = Math.max(itemIndex, indexBeforeDragging);
			let draggedOffset = 0;

			draggingOrder.splice(itemIndex, 0, draggingOrder.splice(draggedIndex, 1)[0]);

			for (let i = 0; i < draggingOrder.length; i++) {
				const item = draggingOrder[i];

				if (item.element !== draggedElement) {
					let offset = 0;

					if (i >= minIndex && i <= maxIndex) {
						if (maxIndex > indexBeforeDragging) {
							offset = -draggedElementRect.height;
							draggedOffset += item.rect.height;
						} else {
							offset = draggedElementRect.height;
							draggedOffset -= item.rect.height;
						}
					}

					item.element.style.transform = `translateY(${offset}px)`;
					item.top = item.rect.top + offset;
					item.bottom = item.rect.bottom + offset;
				}
			}

			draggedElement.style.transform = `translateY(${draggedOffset}px)`;
			draggingOrder[itemIndex].top = draggedElementRect.top + draggedOffset;
			draggingOrder[itemIndex].bottom = draggedElementRect.bottom + draggedOffset;
		}

		if (hoverItem) {
			handleMouseMovementScroll(e, hoverItem);
		}
	}

	function onKeyDown(e) {
		if (e.key === 'Escape' && container && draggedElement) {
			dragDropCleanup();
			e.preventDefault();
			e.stopImmediatePropagation();
			e.stopPropagation();
		}
	}

	function findHoverItem(e) {
		if (!container) return null;

		const boundsEl = getBoundsElement ? getBoundsElement(container) : container;
		const boundsRect = boundsEl.getBoundingClientRect();
		let hoverItem = null;
		const scrollDelta = originalScrollPosition - (scrollContainer?.scrollTop ?? 0);

		if (boundsRect.top <= e.clientY && boundsRect.bottom >= e.clientY) {
			hoverItem = draggingOrder.find(
				(item) => item.top + scrollDelta <= e.clientY && item.bottom + scrollDelta >= e.clientY,
			)?.element;
		} else if (boundsRect.top < e.clientY) {
			hoverItem = draggingOrder.at(-1).element;
		} else if (boundsRect.bottom > e.clientY) {
			hoverItem = draggingOrder[0].element;
		}

		return hoverItem;
	}

	function dragDropCleanup() {
		const itemsToReset = [...draggingOrder];
		draggingOrder = [];

		itemsToReset.forEach((item) => {
			item.element.style.transform = '';
			item.element.style.zIndex = '';
			item.element.style.transition = 'none';
		});
		previewElement?.remove();
		draggedElement?.classList.remove(dropZoneClass);
		document.documentElement.classList.remove('forman-dragging');

		if (onCleanup && container && draggedElement) {
			onCleanup(draggedElement, container);
		}

		previewElement = null;
		container = null;
		draggedElement = null;
		indexBeforeDragging = null;
		draggedElementRect = null;
		scrollContainer = null;
		originalScrollPosition = 0;

		requestAnimationFrame(() => {
			itemsToReset.forEach((item) => {
				item.element.style.transition = '';
			});
		});
	}

	document.addEventListener('mousedown', onMouseDown);
	document.addEventListener('mouseup', onMouseUp);
	document.addEventListener('mousemove', onMouseMove);
	document.addEventListener('keydown', onKeyDown);

	return function destroy() {
		dragDropCleanup();
		document.removeEventListener('mousedown', onMouseDown);
		document.removeEventListener('mouseup', onMouseUp);
		document.removeEventListener('mousemove', onMouseMove);
		document.removeEventListener('keydown', onKeyDown);
	};
}
